import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBookmarks } from '../context/BookmarksContext';
import { useLoginModal } from '../context/LoginModalContext';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import CreateBookmarkFolderModal from '../components/CreateBookmarkFolderModal/CreateBookmarkFolderModal';
import './BookmarksManage.css';

const BookmarksManage = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user } = useAuth();
  const { folders, loaded, deleteFolder, renameFolder } = useBookmarks();
  const { openLoginModal } = useLoginModal();
  const [deleting, setDeleting] = useState(null); // listId
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const startRename = (folder) => {
    if (folder.is_default) return;
    setEditingListId(folder.id);
    setEditingName(folder.name);
  };

  const handleRenameSubmit = async (folder) => {
    const trimmed = editingName.trim();
    if (!trimmed || trimmed === folder.name) {
      setEditingListId(null);
      return;
    }
    setSavingRename(true);
    try {
      await renameFolder(folder.id, trimmed);
      setEditingListId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename list');
    } finally {
      setSavingRename(false);
    }
  };

  const handleDeleteFolder = useCallback(async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? This cannot be undone.`)) return;
    setDeleting(folder.id);
    try {
      await deleteFolder(folder.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to Delete folder');
    } finally {
      setDeleting(null);
    }
  }, [deleteFolder]);

  if (!user) {
    return (
      <div className="bookmarks-manage-page">
        <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <p className="bookmarks-manage-empty">Sign in to manage your bookmarks.</p>
        <button 
          className="create-post-btn login-prompt-btn"
          onClick={openLoginModal}
        >
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
            <polyline points="10 17 15 12 10 7"></polyline>
            <line x1="15" y1="12" x2="3" y2="12"></line>
          </svg>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="bookmarks-manage-page">
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />

      <div className="bookmarks-manage-header">
        <h1 className="bookmarks-manage-title">Your Bookmarks</h1>
        <button 
          className="create-list-page-btn header-create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create new folder
        </button>
      </div>

      {!loaded ? (
        <LoadingSpinner />
      ) : folders.length === 0 ? (
        <p className="bookmarks-manage-empty">No bookmark folders yet. Create one to start saving posts.</p>
      ) : (
        <ul className="bookmarks-manage-list">
          {folders.map(folder => (
            <li key={folder.id} className="bookmarks-manage-item">
              <div className="bookmarks-manage-row">
                <div className="bookmarks-manage-info">
                  {editingListId === folder.id ? (
                    <input
                      type="text"
                      className="bookmarks-manage-rename-input"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(folder);
                        if (e.key === 'Escape') setEditingListId(null);
                      }}
                      autoFocus
                      disabled={savingRename}
                      maxLength={100}
                    />
                  ) : (
                    <span className="bookmarks-manage-name">{folder.name}</span>
                  )}
                  <span className="bookmarks-manage-count">{(folder.items ? folder.items.length : 0)} {(folder.items ? folder.items.length : 0) === 1 ? 'post' : 'posts'}</span>
                </div>

                <div className="bookmarks-manage-actions">
                  {editingListId === folder.id ? (
                    <>
                      <button
                        className="bookmarks-action-btn"
                        onClick={() => handleRenameSubmit(folder)}
                        disabled={savingRename || !editingName.trim()}
                      >
                        {savingRename ? '…' : 'Save'}
                      </button>
                      <button
                        className="bookmarks-action-btn"
                        onClick={() => setEditingListId(null)}
                        disabled={savingRename}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="bookmarks-action-btn"
                        onClick={() => startRename(folder)}
                        disabled={deleting === folder.id || folder.is_default}
                      >
                        Rename
                      </button>
                      <Link
                        to={user ? `/user/${user.username}/bookmarks/${encodeURIComponent(folder.name)}` : `/bookmarks/${folder.id}`}
                        className="bookmarks-action-btn bookmarks-view-btn"
                        title="View folder"
                      >
                        View folder
                      </Link>
                      <button
                        className="bookmarks-action-btn bookmarks-delete-btn"
                        onClick={() => handleDeleteFolder(folder)}
                        disabled={deleting === folder.id || folder.is_default}
                        title="Delete folder"
                      >
                        {deleting === folder.id ? '…' : 'Delete'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
          
          {/* Bottom create button */}
          <div className="bookmarks-manage-bottom-actions">
            <button 
              className="create-list-page-btn bottom-create-btn"
              onClick={() => setShowCreateModal(true)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create new folder
            </button>
          </div>
        </ul>
      )}

      {showCreateModal && (
        <CreateBookmarkFolderModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
};

export default BookmarksManage;


